import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';
import { AnnouncementBanner, AnnouncementPopup } from './components/AnnouncementDisplay';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import WalletPage from './pages/Wallet';
import TransactionHistory from './pages/TransactionHistory';
import MatchLobby from './pages/MatchLobby';
import CreateMatch from './pages/CreateMatch';
import MatchDetail from './pages/MatchDetail';
import MatchConfirm from './pages/MatchConfirm';
import MatchReceipt from './pages/MatchReceipt';
import MatchHistory from './pages/MatchHistory';
import JoinMatch from './pages/JoinMatch';
import AdminPanel from './pages/AdminPanel';
import AdminSettings from './pages/AdminSettings';
import Profile from './pages/Profile';
import ChallengePage from './pages/Challenge';
import QuickMatch from './pages/QuickMatch';
import Leaderboard from './pages/Leaderboard';
import SeasonPass from './pages/SeasonPass';
import WithdrawalPolicy from './pages/WithdrawalPolicy';
import MatchRules from './pages/MatchRules';
import FAQ from './pages/FAQ';
import Support from './pages/Support';
import ContactPublic from './pages/ContactPublic';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AddCredits from './pages/AddCredits';
import VerifyEmailSent from './pages/VerifyEmailSent';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
    if (!loading && adminOnly && user && !user.is_admin) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, adminOnly, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  // If user passed from AuthCallback, render immediately
  if (location.state?.user || user) {
    if (adminOnly && !user?.is_admin) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  }

  return null;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          <WalletPage />
        </ProtectedRoute>
      } />
      <Route path="/add-credits" element={
        <ProtectedRoute>
          <AddCredits />
        </ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute>
          <TransactionHistory />
        </ProtectedRoute>
      } />
      <Route path="/lobby" element={
        <ProtectedRoute>
          <MatchLobby />
        </ProtectedRoute>
      } />
      <Route path="/create-match" element={
        <ProtectedRoute>
          <CreateMatch />
        </ProtectedRoute>
      } />
      <Route path="/match/:matchId" element={
        <ProtectedRoute>
          <MatchDetail />
        </ProtectedRoute>
      } />
      <Route path="/match/:matchId/confirm" element={
        <ProtectedRoute>
          <MatchConfirm />
        </ProtectedRoute>
      } />
      <Route path="/match/:matchId/receipt" element={
        <ProtectedRoute>
          <MatchReceipt />
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <MatchHistory />
        </ProtectedRoute>
      } />
      
      {/* Admin Route */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminPanel />
        </ProtectedRoute>
      } />

      {/* Admin Settings Route */}
      <Route path="/admin/settings" element={
        <ProtectedRoute adminOnly>
          <AdminSettings />
        </ProtectedRoute>
      } />

      {/* Profile Route */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />

      {/* Challenge Route */}
      <Route path="/challenge" element={
        <ProtectedRoute>
          <ChallengePage />
        </ProtectedRoute>
      } />

      {/* Quick Match Route */}
      <Route path="/quick-match" element={
        <ProtectedRoute>
          <QuickMatch />
        </ProtectedRoute>
      } />

      {/* Leaderboard Route */}
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      } />

      {/* Season Pass Route */}
      <Route path="/season-pass" element={
        <ProtectedRoute>
          <SeasonPass />
        </ProtectedRoute>
      } />

      {/* Settings placeholder */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />

      {/* Policy Pages (Public) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/withdrawal-policy" element={<WithdrawalPolicy />} />
      <Route path="/match-rules" element={<MatchRules />} />
      <Route path="/faq" element={<FAQ />} />
      
      {/* Join Match Route (Public - handles auth redirect) */}
      <Route path="/join/:matchId" element={<JoinMatch />} />
      
      {/* Email Verification Routes */}
      <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      
      {/* Password Reset Routes */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Support Pages */}
      <Route path="/support" element={
        <ProtectedRoute>
          <Support />
        </ProtectedRoute>
      } />
      <Route path="/contact" element={<ContactPublic />} />
      <Route path="/help" element={
        <ProtectedRoute>
          <Support />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AnnouncementBanner />
          <Toaster position="top-right" richColors />
          <AppRouter />
          <AnnouncementPopup />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
